import React from 'react';
import { Button, Container, Span } from 'components/Ui';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { isEmpty, get } from 'lodash';

import { dataLayerPush } from 'utils/tracking';
import Loader from 'components/Loader';
import {
  selectCalendarDates,
  selectHasConfirmOrder,
  selectNextDeliveryItems,
  selectSelectedServiceLocation,
  selectDeliveryIsLoading,
  selectIsFirstDelivery,
  selectIsSelectedServiceLocationExcluded,
  selectIsSelectedServiceIndefiniteHold,
  selectCustomerSummary,
} from 'containers/PrimoAccount/selectors';
import {
  getServiceLocationNextDelivery as getServiceLocationNextDeliveryRequest,
  getServiceSummary as getServiceSummaryRequest,
  getBestDeliveryDates as getBestDeliveryDatesRequest,
  loadDeliveryOrder as loadDeliveryOrderRequest,
  loadCalendarDates as loadCalendarDatesRequest,
  removeHold as removeHoldRequest,
} from 'containers/PrimoAccount/actions';
import {
  selectIsHardLockout,
  selectBillingSummary,
  selectIsBillingInformationFetching,
  selectIsEquipmentPickupLockout,
  selectIsCCHold,
  selectUserInfo,
} from 'containers/PrimoProfile/selectors';
import { getBillingSummary as getBillingSummaryRequest } from 'containers/PrimoProfile/actions';
import LockedOut from 'components/Dialogs/LockedOut/Loadable';
import PayBillLockout from 'components/Dialogs/PayBillLockout/Loadable';
import ExclusionLockout from 'components/Dialogs/ExclusionLockout/Loadable';
import IndefiniteHold from 'components/Dialogs/IndefiniteHoldDialog/Loadable';
import ContentfulClient from 'utils/ContentfulClient';
import { getDomainByHost } from 'utils/domainHelper';
import { fetchPage } from 'utils/contentfulUtils';
import { pages } from 'containers/Landing/constants';

import ContentfulFields from 'components/ContentfulFields';
// import FeatureTourDialog from 'components/Dialogs/FeatureTourDialog/index';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import ProcessingModal from '../FutureOrders/ProcessingModal';
import EditDeliveryOrderDrawer from '../FutureOrders/EditDeliveryOrderDrawer';
import DeliveryStatus from './DeliveryStatus';
import PayBillDrawer from './MakePayment/PayBillDrawer';
import YourOrder from './YourOrder';
import OrderEditor from '../OrderEditor';

import styles from './styles.scss';
import { isSupportedModule } from '../utils';

const calculateDeliveryDateRange = (nextDeliveryDate) => {
  const fromDate = (() => {
    const endOfBizToday = moment('18:00:00', 'hh:mm:ss');
    const daysFromToday = moment().isAfter(endOfBizToday) ? 3 : 2;
    return moment().add(daysFromToday, 'days');
  })();

  let toDate = moment(nextDeliveryDate).subtract(2, 'days');

  if (fromDate.isAfter(toDate)) {
    toDate = null;
  }

  return {
    fromDate: fromDate.format('YYYY-MM-DD'),
    toDate: toDate?.format('YYYY-MM-DD'),
  };
};

class NextDelivery extends React.Component {
  state = {
    openPayBill: false,
    page: null,
    hasNextDeliveryDate: true,
    isNextDeliveryLoadEventPushed: false,
  };

  componentDidMount() {
    const { serviceLocation, calendarDates, loadCalendarDates } = this.props;
    if (serviceLocation) {
      this.loadServiceLocationNextDelivery();

      const { fromDate, toDate } = calculateDeliveryDateRange(
        serviceLocation.nextDeliveryDate,
      );
      this.getBestDeliveryDates({
        serviceLocationId: serviceLocation.id,
        fromDate,
        toDate,
        serviceType: get(serviceLocation, 'serviceType', 'water').toLowerCase(),
      });

      if (isEmpty(calendarDates)) {
        const calFromDate = moment().format('YYYY-MM-DD');
        const calToDate = moment().add(90, 'days').format('YYYY-MM-DD');
        loadCalendarDates({
          serviceLocationId: serviceLocation.id,
          fromDate: calFromDate,
          toDate: calToDate,
        });
      }
    }
    this.getEnableEdit();
  }

  componentDidUpdate(prevProps) {
    const {
      serviceLocation,
      calendarDates,
      loadCalendarDates,
      userInfo,
      customerSummary,
    } = this.props;

    if (
      calendarDates !== prevProps.calendarDates &&
      serviceLocation &&
      (!prevProps.serviceLocation ||
        serviceLocation.id !== prevProps.serviceLocation.id)
    ) {
      const calFromDate = moment().format('YYYY-MM-DD');
      const calToDate = moment().add(90, 'days').format('YYYY-MM-DD');
      loadCalendarDates({
        serviceLocationId: serviceLocation && serviceLocation.id,
        fromDate: calFromDate,
        toDate: calToDate,
      });
    }
    if (
      serviceLocation &&
      serviceLocation.nextDeliveryDate &&
      (!prevProps.serviceLocation ||
        serviceLocation.nextDeliveryDate !==
          prevProps.serviceLocation.nextDeliveryDate ||
        serviceLocation.id !== prevProps.serviceLocation.id)
    ) {
      const { fromDate, toDate } = calculateDeliveryDateRange(
        serviceLocation.nextDeliveryDate,
      );

      this.getBestDeliveryDates({
        serviceLocation: serviceLocation.id,
        fromDate,
        toDate,
        serviceType: get(serviceLocation, 'serviceType', 'water').toLowerCase(),
      });
    }

    if (
      userInfo.sub &&
      serviceLocation &&
      customerSummary.profileClass &&
      !this.state.isNextDeliveryLoadEventPushed
    ) {
      dataLayerPush('SelfServe', {
        event: 'next-delivery load',
        user: {
          user_id: userInfo.sub,
          email: userInfo.email,
          first_name: userInfo.firstName,
          last_name: userInfo.lastName,
          accountNumber: customerSummary.accountNumber,
          shipTo: serviceLocation.id,
          deliveryCountry: serviceLocation.countryCode,
          profileClass: customerSummary.profileClass,
          subscriber_id: userInfo.subscriberKey,
          sf_account_id: userInfo.sfAccountId,
          region: serviceLocation.regionName,
          division: serviceLocation.divisionName,
          membership: serviceLocation.membership,
          fdo_date: serviceLocation.fdoDate,
        },
      });
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isNextDeliveryLoadEventPushed: true });
    }
  }

  static getDerivedStateFromProps(props) {
    const { serviceLocation } = props;
    if (serviceLocation && !serviceLocation.nextDeliveryDate) {
      return {
        hasNextDeliveryDate: false,
      };
    }
    return null;
  }

  loadServiceLocationNextDelivery = () =>
    this.props.getServiceLocationNextDelivery();

  loadServiceSummary = () => this.props.getServiceSummary();

  loadBillingSummary = () => this.props.getBillingSummary();

  getBestDeliveryDates = (props) => this.props.getBestDeliveryDates(props);

  setOpenBillDrawer = (open) => this.setState({ openPayBill: open });

  handleOpenPayBillDrawer = () => this.setOpenBillDrawer(true);

  handleClosePayBillDrawer = () => this.setOpenBillDrawer(false);

  getEnableEdit = async () => {
    const {
      isHardLockout,
      isFirstDelivery,
      isEquipmentPickupLockout,
      isSelectedServiceLocationExcluded,
    } = this.props;
    const enableEdit = !(
      isFirstDelivery ||
      isEquipmentPickupLockout ||
      isSelectedServiceLocationExcluded ||
      isHardLockout ||
      this.hasIndefiniteHoldBlock()
    );

    if (enableEdit) {
      const contentfulClient = new ContentfulClient();
      const domain = getDomainByHost(22);
      const page = await fetchPage(
        { ...domain, slug: 'sparkletts' },
        contentfulClient,
        pages.primoNextDelivery,
      );
      this.setState({ page: get(page, 'items[0].fields', null) });
    }
  };

  getLockoutName = () => {
    const { billingSummary, isHardLockout } = this.props;

    let result = '';
    if (isHardLockout) {
      result = billingSummary.lockoutType; // 'HL'
      const lockoutName = billingSummary.lockoutName.toUpperCase();
      if (lockoutName.includes('AGING')) {
        result = 'PAST_DUE_ACCOUNT';
      } else if (lockoutName.includes('CC HOLD')) {
        result = 'CREDIT_CARD_HOLD';
      } else if (lockoutName.includes('NPSS')) {
        result = 'NPSS';
      } else if (lockoutName.includes('NSF HOLD')) {
        result = 'NSF_HOLD';
      }
    }
    return result;
  };

  hasEquipmentPickupLockout = () => {
    const { isEquipmentPickupLockout } = this.props;

    return isEquipmentPickupLockout;
  };

  hasExclusionLockout = () => {
    const { isSelectedServiceLocationExcluded } = this.props;

    return isSelectedServiceLocationExcluded;
  };

  handleBrowseProducts = () => {
    const { history, serviceLocation } = this.props;

    history.push(
      `/account/products/list?serviceLocationId=${serviceLocation.id}`,
    );
  };

  hasIndefiniteHoldBlock = () =>
    this.props.isIndefiniteHold && !this.props.hasConfirmOrder;

  loadDates = () => {
    const { serviceLocation, loadCalendarDates } = this.props;
    this.getEnableEdit();

    if (serviceLocation) {
      const fromDate = moment().format('YYYY-MM-DD');
      const toDate = moment().add(90, 'days').format('YYYY-MM-DD');

      loadCalendarDates({
        serviceLocationId: serviceLocation.id,
        toDate,
        fromDate,
      });
    }
  };

  removeHoldSuccessCallback = () => {
    this.loadDates();
  };

  handleRefresh = () => {
    this.loadDates();
  };

  handleRemoveHold = () => {
    const deliveryOrder = {
      date:
        this.props.serviceLocation &&
        this.props.serviceLocation.nextDeliveryDate,
      cutOffDateTime: moment(
        this.props.serviceLocation &&
          this.props.serviceLocation.nextDeliveryDate,
      ).subtract(1, 'days'),
    };
    this.props.removeHold({
      deliveryOrder,
      successCallback: this.removeHoldSuccessCallback,
    });
  };

  render() {
    const {
      isDeliveryFetching,
      isBillingFetching,
      serviceLocation,
      nextDeliveryItems,
      isFirstDelivery,
      isCCHOLD,
    } = this.props;

    const { page, hasNextDeliveryDate } = this.state;

    const isInactiveShipToLockout = isEmpty(serviceLocation);
    const isFiltrationServiceType = get(
      serviceLocation,
      'serviceType',
      'filtration',
    ).toLowerCase();
    const lockoutName = this.getLockoutName();
    const isLockedOut = !!(lockoutName || isCCHOLD);
    const isEquipmentPickupLockout =
      !isLockedOut && this.hasEquipmentPickupLockout();
    const isExclusionLockout =
      !isInactiveShipToLockout && !isLockedOut && this.hasExclusionLockout();
    const isFiltrationLockout = isFiltrationServiceType === 'filtration';
    const hasLockout =
      isLockedOut ||
      isEquipmentPickupLockout ||
      isExclusionLockout ||
      isInactiveShipToLockout ||
      isFiltrationLockout;

    const deliveryOrder = {
      date: serviceLocation && serviceLocation.nextDeliveryDate,
      cutOffDateTime: moment(
        serviceLocation && serviceLocation.nextDeliveryDate,
      ).subtract(1, 'days'),
    };

    const onEditDeliveryOrder = () => {
      const { loadDeliveryOrder } = this.props;
      localStorage.setItem('nextDeliveryPage', 'true');
      localStorage.removeItem('profilePage');
      loadDeliveryOrder(deliveryOrder);
    };

    const isIndefiniteHoldBlock = this.hasIndefiniteHoldBlock();

    // TODO: Below allows for the showing of a SS top banner
    let bannerModule = null;
    const bottomModules = [];
    if (page && page.contentModules) {
      page.contentModules.forEach((module) => {
        if (get(module, 'sys.contentType.sys.id') === 'selfServeBanner') {
          if (
            serviceLocation &&
            isSupportedModule(module, serviceLocation.brandName)
          ) {
            bannerModule = (
              <ContentfulFields
                type={module.sys.contentType.sys.id}
                document={module}
                key={module.sys.id}
              />
            );
          }
        } else {
          bottomModules.push(
            <ContentfulFields
              type={module.sys.contentType.sys.id}
              document={module}
              key={module.sys.id}
            />,
          );
        }
      });
    }

    return (
      <div className={styles.wrapper}>
        <Loader
          loading={
            hasNextDeliveryDate && (isDeliveryFetching || isBillingFetching)
          }
          local
        >
          {hasLockout ||
          isIndefiniteHoldBlock ||
          (!isEmpty(nextDeliveryItems) && !isEmpty(serviceLocation)) ? (
            <Container value={GTM_TRACKING_TAGS['Next Delivery']}>
              {!isFirstDelivery && !hasLockout && !isIndefiniteHoldBlock && (
                <Loader loading={!page}>{bannerModule}</Loader>
              )}
              {!hasLockout &&
                serviceLocation &&
                serviceLocation.nextDeliveryDate &&
                !isIndefiniteHoldBlock && (
                  <DeliveryStatus
                    deliveryDate={serviceLocation.nextDeliveryDate}
                  />
                )}
              {!hasLockout && !isIndefiniteHoldBlock && <YourOrder />}
              {!isFirstDelivery && !hasLockout && !isIndefiniteHoldBlock && (
                <Loader loading={!page}>{bottomModules}</Loader>
              )}
              {isIndefiniteHoldBlock && !hasLockout && (
                <IndefiniteHold onOpenEditDelivery={onEditDeliveryOrder} />
              )}
              {(isExclusionLockout || isFiltrationLockout) && (
                <ExclusionLockout
                  onOpenPayBill={this.handleOpenPayBillDrawer}
                />
              )}
              {isInactiveShipToLockout && (
                <PayBillLockout
                  isInactiveShipToLockout
                  onOpenPayBill={this.handleOpenPayBillDrawer}
                />
              )}
              {isLockedOut && (
                <LockedOut
                  lockoutName={lockoutName}
                  onOpenPayBill={this.handleOpenPayBillDrawer}
                />
              )}
              {this.state.openPayBill && (
                <PayBillDrawer
                  open={this.state.openPayBill}
                  onClose={this.handleClosePayBillDrawer}
                />
              )}
              {!hasLockout && <OrderEditor />}
            </Container>
          ) : (
            <div className={styles.noDataContainer}>
              <Span>Hang on! There’s nothing in your order...</Span>
              <Span>
                You’re gonna be pretty thirsty. Check out our selection and add
                a few favorites.
              </Span>
              <Button
                text="Browse Products"
                ariaLabel="Browse Products"
                onClick={this.handleBrowseProducts}
              />
            </div>
          )}
          {/* Disabled as per CX-2113, could be enabled in future
          <FeatureTourDialog />
          */}
        </Loader>
        <EditDeliveryOrderDrawer
          onRefresh={this.handleRefresh}
          onRemoveHold={this.handleRemoveHold}
        />
        <ProcessingModal />
      </div>
    );
  }
}

NextDelivery.propTypes = {
  isDeliveryFetching: PropTypes.bool,
  isBillingFetching: PropTypes.bool.isRequired,
  getBillingSummary: PropTypes.func.isRequired,
  getServiceLocationNextDelivery: PropTypes.func.isRequired,
  getServiceSummary: PropTypes.func.isRequired,
  serviceLocation: PropTypes.object,
  nextDeliveryItems: PropTypes.array,
  billingSummary: PropTypes.object,
  isHardLockout: PropTypes.bool.isRequired,
  isFirstDelivery: PropTypes.bool.isRequired,
  isEquipmentPickupLockout: PropTypes.bool.isRequired,
  isSelectedServiceLocationExcluded: PropTypes.bool.isRequired,
  history: PropTypes.object,
  calendarDates: PropTypes.object,
  customerSummary: PropTypes.object,
  userInfo: PropTypes.object,
};

NextDelivery.defaultProps = {
  serviceLocation: {},
  nextDeliveryItems: [],
  loadDeliveryOrder: PropTypes.func.isRequired,
  removeHold: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  calendarDates: selectCalendarDates(),
  hasConfirmOrder: selectHasConfirmOrder(),
  isDeliveryFetching: selectDeliveryIsLoading(),
  serviceLocation: selectSelectedServiceLocation(),
  nextDeliveryItems: selectNextDeliveryItems(),
  billingSummary: selectBillingSummary(),
  isBillingFetching: selectIsBillingInformationFetching(),
  isHardLockout: selectIsHardLockout(),
  isFirstDelivery: selectIsFirstDelivery(),
  isEquipmentPickupLockout: selectIsEquipmentPickupLockout(),
  isSelectedServiceLocationExcluded: selectIsSelectedServiceLocationExcluded(),
  isIndefiniteHold: selectIsSelectedServiceIndefiniteHold(),
  isCCHOLD: selectIsCCHold(),
  userInfo: selectUserInfo(),
  customerSummary: selectCustomerSummary(),
});

const mapDispatchToProps = {
  getBillingSummary: getBillingSummaryRequest,
  getServiceLocationNextDelivery: getServiceLocationNextDeliveryRequest,
  getServiceSummary: getServiceSummaryRequest,
  getBestDeliveryDates: getBestDeliveryDatesRequest,
  loadDeliveryOrder: loadDeliveryOrderRequest,
  loadCalendarDates: loadCalendarDatesRequest,
  removeHold: removeHoldRequest,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(NextDelivery),
);
